import {Box} from '@mantine/core';
import type {FC} from 'react';
import {PMARK_SIGNUP} from '../../../config/constants/routes';
import {CommonLink} from '../../../shared/components/CommonLink';
import {SectionTitle} from '../../../shared/components/SectionTitle';

const TEXT_CLASSNAME = 'leading mb-16 text-black-1 text-base';
const TABLE_TD_CLASSNAME =
  'leading text-black-1 text-base border-black-1 border-collapse border-[1px]';
const EXTERNAL_LINK_OPTIONS = {
  target: '_blank',
  rel: 'noreferrer',
} as const;

type Props = {
  isRuleConfirm?: boolean;
};

export const PrivacyPolicyContainer: FC<Props> = () => {
  return (
    <>
      <SectionTitle
        order={2}
        title="外資就活ドットコムの利用に関するプライバシーポリシー"
      />
      <Box className="mb-16 bg-white px-8 py-16 pc:px-32">
        <div className="mb-16">
          <p className={`${TEXT_CLASSNAME} text-left`}>
            本プライバシーポリシーは、外資就活ドットコムのアプリ（以下「本件アプリ」といいます。）および本件アプリ又は各種ウェブ閲覧ソフト（本件アプリと総称して以下「本件アプリ等」といいます。）
            を通じて提供されるサービス（以下「本件サービス」といいます。）のご利用に当たり、
            利用者の皆様が株式会社ハウテレビジョン（以下「当社」といいます。）と当社が提携する各企業に対して提供される情報（以下「情報」といいます。）の収集、使用、共有および保護に関して規定しています。
          </p>
          <p className={`${TEXT_CLASSNAME} text-left`}>
            当社は、利用者のプライバシーの保護を重視し、尊重することをお約束します。
            本プライバシーポリシーは、本件サービスに関して、当社と当社が提携する各企業が情報をどのように収集し、使用し、共有し、保護するのか、利用者が情報をどのように修正し、削除することができるか、などを説明することにより、利用者のプライバシーについて透明性を高め、利用者の権利を保護することを目的としています。
          </p>
          <p className={`${TEXT_CLASSNAME} text-left underline`}>
            利用者は、本件アプリ等を用いて本件サービスを利用する前に、必ず本プライバシーポリシーをお読みください。
          </p>

          <p className="mb-12 bg-background-1 px-8 py-4 text-md font-bold leading text-black-1">
            1. 当社が収集・利用する情報およびその目的
          </p>
          <p className={`${TEXT_CLASSNAME} text-left`}>
            当社は、本件サービスのご利用に当たり、利用者に対して、以下に定める情報の提供をお願いし、かかる情報を以下に定める目的のために利用します。
            利用者は、本件アプリ等を用いて以下の情報を提供する際に、当社が以下に定める目的のために当該情報を利用することに同意します。
          </p>
          <p className={`${TEXT_CLASSNAME} text-left`}>
            当社は、提供いただいた情報のうち個人情報の取扱いについては、本プライバシーポリシーのほか、
            <CommonLink
              href={PMARK_SIGNUP}
              className="text-link-1 underline hover:opacity-80"
              {...EXTERNAL_LINK_OPTIONS}
            >
              個人情報の取扱い
            </CommonLink>
            にしたがって取り扱うものとします。
          </p>
          <table className="mb-12">
            <thead>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <strong>利用者が提供する情報</strong>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <strong>当社の利用の目的</strong>
                </td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>メールアドレス</td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  大学生であることの確認
                  <br />
                  ユーザー認証
                  <br />
                  ステータスの保存・引継ぎ
                  <br />
                  マーケティング調査・統計・分析
                  <br />
                  お問い合わせ対応
                </td>
              </tr>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>生年月日</td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  年齢確認
                  <br />
                  お問い合わせ対応
                </td>
              </tr>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>性別・氏名</td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  ステータスの保存・引継ぎ
                  <br />
                  マーケティング調査・統計・分析
                  <br />
                  お問い合わせ対応
                </td>
              </tr>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>電話番号</td>
                <td className={`${TABLE_TD_CLASSNAME}`}>お問い合わせ対応</td>
              </tr>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  学校情報・経歴・スキル
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  ステータスの保存・引継ぎ
                  <br />
                  マーケティング調査・統計・分析
                  <br />
                  お問い合わせ対応
                </td>
              </tr>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  第三者のサービスにかかるアカウント情報、個人のサイト、ブログ等のURL
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  スカウト配信その他当社サービス提供のため
                </td>
              </tr>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>履歴書</td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  キャリア・コンサルティングサービス、求人案件の紹介その他当社サービス提供のため
                </td>
              </tr>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  選考体験記、当社からのインタビューへの回答その他の利用者が当社に提供した情報
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  当社サービス上での表示その他当社サービスの提供、改善のため
                  <br />
                  マーケティング調査・統計・分析
                  <br /> お問い合わせ対応
                </td>
              </tr>
            </tbody>
          </table>
          <p className={`${TEXT_CLASSNAME} mb-12`}>
            ※なお、当社では、本件サービスの利用者が入力したメールアドレスが、大学が発行したメールアドレスであるか否かを判定することを通じて、利用者が18歳未満でないことを確認します。当社は、本件サービスに関連して、18歳未満の子供に対して製品およびサービスを提供し、又は、それを認識しながら子供から情報を収集しもしくは勧誘することはありません。
          </p>

          <p className="mb-12 bg-background-1 px-8 py-4 text-md font-bold leading text-black-1">
            2. 情報の利用に対する利用者の同意の変更の方法
          </p>
          <p className={`${TEXT_CLASSNAME} text-left`}>
            利用者は、いつでも、本件アプリ等を用いてアクセス可能な本サービスのマイページ（以下「マイページ」といいます。）にアクセスし、自ら提供した情報を確認することができます。
            また、利用者は、マイページにアクセスし、自ら提供した情報を修正又は削除することにより、修正又は削除された情報を当社が利用することを停止させることができます（当社は、修正後の情報を引き続き利用いたします。）。
            なお、１．の「利用者が提供する情報」をご提供いただけない場合、本件アプリ又は本件サービスの全部又は一部をご利用いただけないことがございます。
          </p>
          <p className={`${TEXT_CLASSNAME} text-left`}>
            利用者は、退会することにより、当社が１．の「利用者が提供する情報」を利用することを停止させることができます。
          </p>
          <p className={`${TEXT_CLASSNAME} text-left`}>
            修正、削除又はアカウント削除の時点までに利用者が提供した情報につき、当社は、関連法規および社内規定に従って、個人を特定し得る情報に対してはマスキング処理を施し、マーケティング調査・統計・分析等に利用することがあります。
          </p>

          <p className="mb-12 bg-background-1 px-8 py-4 text-md font-bold leading text-black-1">
            3. 当社による情報の保護・削除の方針
          </p>
          <p className={`${TEXT_CLASSNAME} text-left`}>
            当社は、セキュリティ面で十分な保証を提供する第三者サービスを採用します。当社では、当社が自らに課すセキュリティ措置と同等の措置を第三者サービスにも要求しています。
            当社はすべての第三者サービスと契約を締結し、それにより情報の保護を確保しています。
          </p>
          <p className={`${TEXT_CLASSNAME} text-left`}>
            当社は利用者の個人情報およびプライバシーを保護するために最善を尽しますが、それらの情報が確実に安全であると保証することはできません。
          </p>

          <p className="mb-12 bg-background-1 px-8 py-4 text-md font-bold leading text-black-1">
            4. 第三者提供
          </p>
          <p className={`${TEXT_CLASSNAME} mb-8 text-left`}>
            （１）当社は、以下に掲げる場合を除き、事前に利用者の同意を得ないで第三者に提供することはありません。
          </p>
          <p className={`${TEXT_CLASSNAME} mb-8 pl-16 text-left`}>
            a.法令に基づく場合
          </p>
          <p className={`${TEXT_CLASSNAME} mb-8 pl-16 text-left`}>
            b.人の生命、身体又は財産の保護のために必要がある場合であって、本人の同意を得る事が困難であるとき
          </p>
          <p className={`${TEXT_CLASSNAME} mb-8 pl-16 text-left`}>
            c.公衆衛生の向上又は児童の健全な育成の推進のために特に必要がある場合であって、本人の同意を得る事が困難であるとき
          </p>
          <p className={`${TEXT_CLASSNAME} mb-8 pl-16 text-left`}>
            d.国の機関若しくは地方公共団体又はその委託を受けた者が法令の定める事務を遂行することに対して協力する必要がある場合であって、
            本人の同意を得ることによって当該事務の遂行に支障を及ぼすおそれがあるとき
          </p>
          <p className={`${TEXT_CLASSNAME} pl-16 text-left`}>
            e.その他法令で認められる場合
          </p>
          <p className={`${TEXT_CLASSNAME} text-left`}>
            （２）上記(1)にかかわらず、当社は、利用者の興味・関心等に合わせ個別に最適化された本件サービスの提供（本件サービスに関する
            各種広告等の配信（本サービス以外のWEBサイト、SNSなどでの配信を含みます。）
            、ポップアップでの通知の表示、電子メールの送付等を含みます。）のために、それ自体では特定の個人を識別できないようにした情報（利用者が提供したメールアドレスをハッシュ化したデータ、大学名、学部名、学部の分類（文理）、卒業予定年度、志望業界、サークル名、外資就活ドットコムの利用履歴等、Cookie、広告識別子（Identification
            For Advertisers、Google advertising ID））
            を当社が提携する各企業に提供する場合には、当社が取り扱う個人情報を第三者に提供することがあります。
          </p>

          <p className="mb-12 bg-background-1 px-8 py-4 text-md font-bold leading text-black-1">
            5. 第三国への移転
          </p>
          <p className={`${TEXT_CLASSNAME} text-left`}>
            利用者は、本プライバシーポリシーを受諾することにより、欧州連合域外の以下の第三国に情報が移転、保管、処理およびその他の形で使用される可能性があることに同意します。
            その結果、情報が利用者の居住国の法令および規則と異なるプライバシーおよびデータの保護に関する法律の適用を受ける可能性があることを利用者には承諾いただきます。
          </p>
          <p className={`${TEXT_CLASSNAME} pl-32 text-left`}>
            アメリカ合衆国（
            <CommonLink
              href="https://www.ppc.go.jp/enforcement/infoprovision/laws/offshore_report_america/"
              className="text-link-1 underline hover:opacity-80"
            >
              個人情報の保護に関する制度
            </CommonLink>
            ）
          </p>

          <p className="mb-12 bg-background-1 px-8 py-4 text-md font-bold leading text-black-1">
            6. 他のウェブサイト
          </p>
          <p className={`${TEXT_CLASSNAME} text-left`}>
            本件アプリ等によって、利用者は第三者から提供されるアプリケーション、ウェブサイト、製品およびサービスを閲覧、訪問又は使用できる場合があります。
            第三者が収集する情報（これには位置情報、連絡先又は個人を識別できる情報等が含まれることがあります）は、それら第三者のプライバシーポリシーが適用されます。
            当社は、利用者が一切の情報をかかる第三者へ提供する前にそのプライバシーポリシーを確認することを推奨します。
          </p>

          <p className="mb-12 bg-background-1 px-8 py-4 text-md font-bold leading text-black-1">
            7. Cookieポリシー（外部送信に係る利用者に関する情報の取扱い）
          </p>
          <p className={`${TEXT_CLASSNAME} mb-8 text-left`}>
            （１）Cookieの使用
          </p>
          <p className={`${TEXT_CLASSNAME} text-left`}>
            当社は、本件サービスにおいて、Cookieおよびこれに類する技術（以下「Cookie等」といいます。）を使用する場合があります。
          </p>
          <p className={`${TEXT_CLASSNAME} mb-8 text-left`}>（２）Cookieとは</p>
          <p className={`${TEXT_CLASSNAME} text-left`}>
            Cookieとは、利用者がウェブサイトにアクセスした際に、ウェブサーバが利用者のコンピュータ（PC、スマートフォンやタブレット等インターネットに接続可能な機器）を識別する業界標準の技術です。Cookieは、利用者のコンピュータを識別することはできますが、利用者個人を識別することはできません。なお、ブラウザの設定の変更によりCookieの機能を無効にすることはできますが、本件サービスの全部又は一部が利用できなくなる場合があります。
          </p>
          <p className={`${TEXT_CLASSNAME} mb-8 text-left`}>
            （３）主な利用サービスと利用目的等
          </p>
          <p className={`${TEXT_CLASSNAME} text-left`}>
            当社は、利用者への本件サービス、各種機能（外部サービスとの連携を含む）の提供のため、また本件サービスの利用状況を分析し、パフォーマンス改善やサービスの向上・改善を行うため、Cookie等を使用し、主にIPアドレス、閲覧した本件サービスにかかるWebページのタイトル、URL等の情報を自動で収集し、利用します。
            利用者は、本件アプリ等を用いた本件サービスの利用を継続することをもって、
            当社が当該目的のために当該情報を利用することに同意するものとします。
          </p>
          <p className={`${TEXT_CLASSNAME} mb-12`}>
            ※Google Calendar APIの利用について
          </p>
          <ul className={`${TEXT_CLASSNAME} mb-12 ml-16`}>
            <li className={`${TEXT_CLASSNAME} mb-12 list-disc`}>
              本件サービスでは、本件サービスのカレンダーの予定をGoogleカレンダーにも登録（カレンダーの作成、変更、削除、個々の予定の更新等を含みます。）する機能（以下「カレンダー同期機能」といいます。）のためにGoogle
              Calendar APIを利用しています。
            </li>
            <li className={`${TEXT_CLASSNAME} mb-12 list-disc`}>
              Google Calendar
              APIから取得した情報は、カレンダー同期機能でのみ利用しており、本サービスのカレンダーの予定をGoogleカレンダーにも登録（カレンダーの作成、変更、削除、個々の予定の更新等を含みます。）する用途以外では利用しません。
            </li>
            <li className={`${TEXT_CLASSNAME} mb-12 list-disc`}>
              カレンダー同期機能の利用は、利用者が当該機能を利用するためにGoogleの認証ページで同期を許可した場合に限ります。
            </li>
            <li className={`${TEXT_CLASSNAME} mb-12 list-disc`}>
              Google Calendar
              APIおよびGoogleのプライバシーポリシーについては以下をご覧ください。
              <br />
              Google Calendar API：https://developers.google.com/calendar
              <br />
              Googleのプライバシーポリシー :
              https://policies.google.com/privacy?hl=ja&gl=ZZ
            </li>
          </ul>
          <p className={`${TEXT_CLASSNAME} text-left`}>
            また、利用者の本件サービスの利用状況を分析し、パフォーマンス改善やサービスの向上・改善を行うため、各種機能を提供するため、また本件サービスにかかる広告配信のため、以下のサービスを利用し、当該サービス提供者に対して、主に閲覧した本件サービスにかかるWebページのタイトル、URL、閲覧日時等の本件サービスの利用履歴・行動履歴、端末識別子、OSバージョン、広告識別子等の情報が提供されることがあります。利用者は、本件アプリ等を用いた本件サービスの利用を継続することをもって、
            当該サービス提供者が当該目的のために当該情報を利用することに同意するものとします。
          </p>
          <p className={`${TEXT_CLASSNAME} text-left`}>
            当該サービス提供者が定めるオプトアウトの方法によって当該サービス提供者への情報提供を停止することができます。ただし、これらのサービス提供者への情報提供を停止した場合には、各サービス提供者のサービスを活用する上で制約を受ける場合がありますのでご留意ください。
          </p>
          <table className="mb-12">
            <thead>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <strong>利用サービス</strong>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <strong>サービス提供者</strong>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <strong>プライバシーポリシー等</strong>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <strong>利用目的</strong>
                </td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://developers.google.com/analytics?hl=ja"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    Google Analytics
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>Google LLC</td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://policies.google.com/privacy"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    参照リンク
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  サービス利用状況を分析し、パフォーマンス改善やサービスの向上・改善を行うため
                </td>
              </tr>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://marketingplatform.google.com/about/tag-manager/"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    Google Tag Manager
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>Google LLC</td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://policies.google.com/privacy"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    参照リンク
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  サービス利用状況を分析し、パフォーマンス改善やサービスの向上・改善を行うため
                </td>
              </tr>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://search.google.com/search-console/about?hl=ja"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    Google Search Console
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>Google LLC</td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://policies.google.com/privacy"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    参照リンク
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  サービス利用状況を分析し、パフォーマンス改善やサービスの向上・改善を行うため
                </td>
              </tr>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://firebase.google.com/?hl=ja"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    Firebase
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>Google LLC</td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://firebase.google.com/support/privacy?hl=ja"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    参照リンク
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  サービス利用状況を分析し、パフォーマンス改善やサービスの向上・改善を行うため
                </td>
              </tr>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://admob.google.com/intl/ja/home/"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    Google AdMob
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>Google LLC</td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://policies.google.com/privacy"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    参照リンク
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  サービス内に広告を表示するためと広告データを取得するため
                </td>
              </tr>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://cloud.google.com/bigquery?hl=ja"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    BigQuery
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>Google LLC</td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://policies.google.com/privacy"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    参照リンク
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  サービス利用状況を分析し、パフォーマンス改善やサービスの向上・改善を行うため
                </td>
              </tr>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://admanager.google.com/intl/ja/home/"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    Googleアドマネージャー
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>Google LLC</td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://policies.google.com/privacy"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    参照リンク
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  サービス内に広告を表示するためと広告データを取得するため
                </td>
              </tr>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://www.appsflyer.com/ja/"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    AppsFlyer
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>AppsFlyer</td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://www.appsflyer.com/legal/services-privacy-policy/"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    参照リンク
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  アプリのダウンロード状況のデータを取得、分析し、パフォーマンス改善やサービスの向上・改善を行うためするため
                </td>
              </tr>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://developer.apple.com/jp/app-store-connect/"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    App Store Connect
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>Apple Inc.</td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://www.apple.com/legal/privacy/jp/"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    参照リンク
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  iOSデバイスにかかるサービス利用状況を分析し、パフォーマンス改善やサービスの向上・改善を行うため
                </td>
              </tr>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://ahrefs.jp/"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    Ahrefs
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>Ahrefs Pte.Ltd</td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://ahrefs.com/legal/privacy-policy"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    参照リンク
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  検索エンジンにおけるパフォーマンスを分析し、パフォーマンス改善やサービスの向上・改善を行うため
                </td>
              </tr>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://mieru-ca.com/"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    ミエルカSEO
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  株式会社Faber Company
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://mieru-ca.com/privacy-policy/"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    参照リンク
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  サービス利用状況を分析し、パフォーマンス改善やサービスの向上・改善を行うため
                </td>
              </tr>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://www.tableau.com/ja-jp/why-tableau/what-is-tableau"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    Tableau
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  TABLEAU SOFTWARE, LLC
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://www.salesforce.com/jp/company/privacy/"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    参照リンク
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  サービス利用状況を分析し、パフォーマンス改善やサービスの向上・改善を行うため
                </td>
              </tr>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://www.appier.com/ja-jp/products/aiqua"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    AIQUA
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  Appier Japan株式会社
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://www.appier.com/ja-jp/about/privacy-policy"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    参照リンク
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  サービスの利用状況を分析して、利用者個別に最適化された本件サービスに関する広告の配信、ポップアップでの通知の表示、電子メールの送付等を行うため
                </td>
              </tr>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://www.bugsnag.com/"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    Bugsnag
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  SmartBear Software Inc.
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://smartbear.com/privacy/"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    参照リンク
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  サービス利用状況を分析し、パフォーマンス改善やサービスの向上・改善を行うため
                </td>
              </tr>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>Meta広告</td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  Meta Platforms, Inc.
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://ja-jp.facebook.com/privacy/policy"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    参照リンク
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  metaが運営するプラットフォームに広告を配信するため
                </td>
              </tr>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://business.twitter.com/ja/advertising.html"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    Twitter広告
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>X Corp.</td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://twitter.com/ja/privacy"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    参照リンク
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  Twitterに広告を配信するため
                </td>
              </tr>
              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://www.linebiz.com/jp/service/line-ads/"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    LINE広告
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>LINE株式会社</td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://line.me/ja/terms/policy/"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    参照リンク
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  LINEに広告を配信するため
                </td>
              </tr>

              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://clarity.microsoft.com/"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    Clarity
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  Microsoft Corporation
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://www.microsoft.com/privacy/privacystatement"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    参照リンク
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  サービス利用状況を分析し、サービスの向上・改善やマーケティング改善を行うため
                </td>
              </tr>

              <tr>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://www.capi-co.net/"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    CAPiCO
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>株式会社イルグルム</td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  <CommonLink
                    href="https://yrglm.co.jp/policy/"
                    className="text-link-1 underline hover:opacity-80"
                    {...EXTERNAL_LINK_OPTIONS}
                  >
                    参照リンク
                  </CommonLink>
                </td>
                <td className={`${TABLE_TD_CLASSNAME}`}>
                  サービス利用状況を分析して、利用者個別に最適化された本件サービスに関する広告を配信するため
                </td>
              </tr>
            </tbody>
          </table>

          <p className="mb-12 bg-background-1 px-8 py-4 text-md font-bold leading text-black-1">
            8. 本プライバシーポリシーの変更
          </p>
          <p className={`${TEXT_CLASSNAME} mb-12`}>
            当社は、本プライバシーポリシーを随時変更できるものとします。重要な変更については、利用者への通知又は公表を行います。
          </p>
          <p className="mb-12 bg-background-1 px-8 py-4 text-md font-bold leading text-black-1">
            9. 連絡窓口
          </p>
          <p className={`${TEXT_CLASSNAME} mb-12`}>
            利用者は、日本の法令に従って利用者の権利を行使することができます。
            かかる権利を行使する場合、利用者は、上記2.に定める手続で確認、修正又は削除を行うこと、および上記2.に定める手続では達成できない事項について以下の連絡窓口に連絡することができます。
          </p>
          <p className={`${TEXT_CLASSNAME} mb-12`}>
            株式会社ハウテレビジョン 個人情報問合せ窓口
            <br />
            〒107-6032 東京都港区赤坂1-12-32 アーク森ビル32F
            <br />
            メールアドレス: soumu@howtelevision.jp TEL: 03-6427-2862
            <br />
            （電話の受付時間 10:00～19:00
            ※土・日曜日、祝日、年末年始、ゴールデンウィークを除く)
          </p>

          <p className="mb-12 bg-background-1 px-8 py-4 text-md font-bold leading text-black-1">
            10. その他の条項
          </p>
          <p className={`${TEXT_CLASSNAME} mb-12`}>
            利用者が提供した情報の一部は、利用者自身がマイページにアクセスすることにより表示されますが、
            それ以外の情報の開示を請求される場合には、当社の個人情報保護方針で定めた手続きに従って開示手数料をいただくことがあります。
          </p>
          <p className={`${TEXT_CLASSNAME} mb-12`}>
            本プライバシーポリシーに定めのない事項については、当社の個人情報保護方針および個人情報に関する公表文（
            <CommonLink
              href={'/corporate_privacy_policy'}
              className="text-link-1 underline hover:opacity-80"
              {...EXTERNAL_LINK_OPTIONS}
            >
              https://gaishishukatsu.com/corporate_privacy_policy
            </CommonLink>
            ）が適用されます。
          </p>
          <p className={`${TEXT_CLASSNAME} mb-12`}>
            本プライバシーポリシーと当社の個人情報保護方針との間で齟齬がある場合は、本プライバシーポリシーの規定が優先されます。
          </p>
          <p className={`${TEXT_CLASSNAME} mb-12`}>
            附則
            <br />
            発効日: 2018年12月6日
            <br />
            改定日: 2020年3月2日 / 適用日; 2020年3月2日
            <br />
            改定日: 2020年4月17日 / 適用日: 2020年4月17日
            <br />
            改定日: 2020年6月8日 / 適用日: 2020年6月8日
            <br />
            改定日: 2020年8月31日 / 適用日: 2020年8月31日
            <br />
            改定日: 2021年5月19日 / 適用日: 2021年5月19日
            <br />
            改定日: 2023年6月15日 / 適用日: 2023年6月15日
            <br />
            改定日: 2025年1月20日 / 適用日: 2025年1月20日
            <br />
            改定日: 2025年4月22日 / 適用日: 2025年4月22日
            <br />
            改定日: 2025年7月7日 / 適用日: 2025年7月7日
            <br />
            <br />
            Copyright © 2018 Howtelevision, Inc. All Rights Reserved.
          </p>
        </div>
      </Box>
    </>
  );
};
